// Shipping service for API calls
import { ShippingRate, ShippingLocation } from '../types';
import { fetchWithErrorHandling, guestPrefix } from './apiUtils';

export interface ShippingRatesRequest {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalcode: string;
  countrycode?: string;
  phone?: string;
  residential?: boolean;
  skip_validation?: boolean;
}

export interface ShippingRatesApiResponse {
  data: ShippingRate[];
  meta: {
    addressValidated: boolean;
    validationWarnings: Record<string, string>;
  };
}

export interface ShippingRatesResult {
  rates: ShippingRate[];
  addressValidated: boolean;
  validationWarnings: string[];
}

export class ShippingService {
  /**
   * Get shipping rates for a given address.
   * Uses POST /api/v2/shipping/rates (authenticated) or /api/v2/guest/shipping/rates (guest).
   */
  static async getShippingRates(
    address: ShippingRatesRequest,
    skipValidation = false
  ): Promise<ShippingRatesResult> {
    try {
      const endpoint = `/api/v2${guestPrefix()}/shipping/rates`;

      const response = await fetchWithErrorHandling<ShippingRatesApiResponse>(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          address1: address.address1,
          address2: address.address2 || '',
          city: address.city,
          state: address.state,
          postalcode: address.postalcode,
          countrycode: address.countrycode || 'US',
          phone: address.phone,
          residential: address.residential ?? true,
          skip_validation: skipValidation,
        }),
      });

      return {
        rates: response.data,
        addressValidated: response.meta.addressValidated,
        validationWarnings: Object.values(response.meta.validationWarnings || {}),
      };
    } catch (error) {
      console.error('Failed to fetch shipping rates:', error);
      throw new Error('Unable to load shipping rates. Please try again later.');
    }
  }

  /**
   * Convert a ShippingLocation to a ShippingRatesRequest
   */
  static locationToRequest(location: ShippingLocation): ShippingRatesRequest {
    return {
      address1: location.address1,
      address2: location.address2 || undefined,
      city: location.city,
      state: location.state,
      postalcode: location.postalcode || location.zip,
      countrycode: location.countrycode || 'US',
      phone: location.phone || undefined,
      residential: location.residential,
    };
  }

  /**
   * Convert a partial new address form to a ShippingRatesRequest
   */
  static newAddressToRequest(address: Partial<ShippingLocation>): ShippingRatesRequest | null {
    const isOtherInternational = address.countrycode === 'ZZ';

    // Validate required fields (zip optional for "Other International" addresses)
    if (!address.address1 || !address.city || !address.state) {
      return null;
    }
    if (!isOtherInternational && !address.zip) {
      return null;
    }

    return {
      address1: address.address1,
      address2: address.address2 || undefined,
      city: address.city,
      state: address.state,
      postalcode: address.zip || '',
      countrycode: address.countrycode || 'US',
      phone: address.phone || undefined,
      residential: true, // New addresses default to residential
    };
  }
}
